import { zodResolver } from "@hookform/resolvers/zod";
import type { ComponentProps } from "react";
import { useMemo, useState, type FormEvent } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { z } from "zod";

import {
  getLandingPrivacyUrl,
  getLandingTermsUrl,
} from "@/lib/landingLinks";
import { cn } from "@/lib/utils";
import { ArrowLeft, Briefcase, Loader2, User } from "lucide-react";
import { apiBaseUrl } from "@/services/apiClient";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { CodeInput } from "@/components/shared/CodeInput";
import { useAuth } from "@/contexts/AuthContext";
import {
  isPlatformOtpRequiredResponse,
  isTotpRequiredResponse,
  requiresTotpEnrollment,
} from "@/services/authService";

type LoginMode = "magic_link" | "password";

interface LoginFormBranding {
  logoUrl?: string;
  logoAlt?: string;
  title?: string;
  hideGoogleSso?: boolean;
  hideRegister?: boolean;
}

type LoginType = "user" | "professional";

export function LoginForm({
  className,
  returnTo = "/",
  branding,
  showLoginTypeSelector = false,
  chrome = "card",
  onRolePreview,
  ...props
}: ComponentProps<"div"> & {
  returnTo?: string;
  branding?: LoginFormBranding;
  showLoginTypeSelector?: boolean;
  /** "bare" lets the PAGE own the shell; "card" is the self-contained default. */
  chrome?: "card" | "bare";
  /**
   * Which role the user is previewing (hover or keyboard focus), or null when
   * they are over neither. Lets a page react — the full-bleed sign-in uses it
   * to swap the cover copy, which is the only place the invitation-only rule
   * (decision e7984b6) is stated.
   */
  onRolePreview?: (role: LoginType | null) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const privacyUrl = getLandingPrivacyUrl(locale);
  const termsUrl = getLandingTermsUrl(locale);
  const {
    requestLoginLink,
    loginWithPassword,
    submitPlatformLoginOtp,
    resendPlatformLoginOtp,
    submitProfessionalTotp,
    submitProfessionalTotpRecovery,
  } = useAuth();
  const [mode, setMode] = useState<LoginMode>("magic_link");
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const [platformOtpStep, setPlatformOtpStep] = useState<{
    pendingLoginId: string;
    email: string;
  } | null>(null);
  const [otpCode, setOtpCode] = useState("");
  /**
   * The professional second factor. Separate state from the platform OTP: the
   * two challenges are different factors with different recovery stories, and
   * sharing one state object is how a "resend" button ends up on a screen
   * where there is nothing to resend.
   */
  const [totpStep, setTotpStep] = useState<{
    pendingLoginId: string;
  } | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpBusy, setTotpBusy] = useState(false);
  const [usingRecoveryCode, setUsingRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [otpVerifyBusy, setOtpVerifyBusy] = useState(false);
  const [otpResendBusy, setOtpResendBusy] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [loginType, setLoginType] = useState<LoginType>("user");
  const [formStep, setFormStep] = useState<"role" | "auth">(
    showLoginTypeSelector ? "role" : "auth",
  );

  const hideGoogleSso = branding?.hideGoogleSso || loginType === "professional";
  const hideRegister = branding?.hideRegister || loginType === "professional";


  const handleRoleSelect = (role: LoginType): void => {
    // Past the role step the choice is made, so the cover copy follows it
    // rather than reverting to neutral for the rest of the flow.
    onRolePreview?.(role);
    setLoginType(role);
    setMode("magic_link");
    setFormStep("auth");
  };

  const localeForApi = (locale.startsWith("es") ? "es" : "en") as "en" | "es";

  const loginSchema = useMemo(
    () =>
      z.object({
        email: z.string().min(1, t("validation.emailRequired")).email(t("validation.emailInvalid")),
        password: z.string().optional(),
      }),
    [t]
  );
  type LoginFormValues = z.infer<typeof loginSchema>;

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const password = watch("password");

  const onSubmit = async (values: LoginFormValues): Promise<void> => {
    if (mode === "password") {
      const p = values.password?.trim();
      if (!p) {
        setError("password", {
          message: t("validation.required", "This field is required."),
          type: "required",
        });
        return;
      }
    }
    try {
      if (mode === "password" && values.password) {
        const loginResult = await loginWithPassword(
          values.email,
          values.password.trim(),
          localeForApi
        );
        if (isPlatformOtpRequiredResponse(loginResult)) {
          setPlatformOtpStep({
            pendingLoginId: loginResult.pendingLoginId,
            email: values.email.trim().toLowerCase(),
          });
          setOtpCode("");
          return;
        }
        if (isTotpRequiredResponse(loginResult)) {
          setTotpStep({ pendingLoginId: loginResult.pendingLoginId });
          setTotpCode("");
          setRecoveryCode("");
          setUsingRecoveryCode(false);
          return;
        }
        // An un-enrolled professional was given a session so they CAN enroll;
        // routing them there directly saves them collecting a 403 from
        // whatever they tried to open first.
        if (requiresTotpEnrollment(loginResult)) {
          navigate("/security/two-factor", { replace: true });
          return;
        }
        navigate(returnTo, { replace: true });
        return;
      }
      await requestLoginLink(values.email, locale, returnTo !== "/" ? returnTo : undefined);
      setSentEmail(values.email);
      setSent(true);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const message =
        mode === "password"
          ? t("auth.login.invalidCredentials", "Invalid email or password.")
          : status === 404
            ? t("auth.noAccountForEmail", "No account found for this email. Please register first.")
            : t("auth.login.error", "Something went wrong. Try again.");
      toast.error(message);
    }
  };

  const handleResend = (): void => {
    setSent(false);
  };

  const handleSubmitOtp = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (platformOtpStep == null) {
      return;
    }
    const digits = otpCode.replace(/\D/g, "").slice(0, 6);
    if (digits.length !== 6) {
      toast.error(t("auth.login.platformOtp.codeInvalid"));
      return;
    }
    setOtpVerifyBusy(true);
    try {
      await submitPlatformLoginOtp(platformOtpStep.pendingLoginId, digits);
      navigate(returnTo, { replace: true });
    } catch {
      toast.error(t("auth.login.platformOtp.invalidCode"));
    } finally {
      setOtpVerifyBusy(false);
    }
  };

  const handleResendOtp = async (): Promise<void> => {
    if (platformOtpStep == null) {
      return;
    }
    setOtpResendBusy(true);
    try {
      await resendPlatformLoginOtp(platformOtpStep.pendingLoginId, localeForApi);
      toast.success(t("auth.login.platformOtp.resendSuccess"));
    } catch {
      toast.error(t("auth.login.platformOtp.resendError"));
    } finally {
      setOtpResendBusy(false);
    }
  };

  const handleSubmitTotp = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (totpStep == null) {
      return;
    }
    setTotpBusy(true);
    try {
      if (usingRecoveryCode) {
        await submitProfessionalTotpRecovery(
          totpStep.pendingLoginId,
          recoveryCode
        );
      } else {
        const digits = totpCode.replace(/\D/g, "").slice(0, 6);
        if (digits.length !== 6) {
          toast.error(t("auth.login.totp.codeInvalid"));
          return;
        }
        await submitProfessionalTotp(totpStep.pendingLoginId, digits);
      }
      navigate(returnTo, { replace: true });
    } catch {
      // ONE message for every failure, matching the server. Saying whether the
      // code was wrong, stale, or the challenge expired would tell an attacker
      // which half of the login they already have.
      toast.error(t("auth.login.totp.invalidCode"));
    } finally {
      setTotpBusy(false);
    }
  };

  const handleBackFromTotp = (): void => {
    // Clearing the challenge id too: the server already burned it on the
    // attempt cap, and keeping it would let a second try reuse a dead one.
    setTotpStep(null);
    setTotpCode("");
    setRecoveryCode("");
    setUsingRecoveryCode(false);
  };

  const handleBackFromOtp = (): void => {
    setPlatformOtpStep(null);
    setOtpCode("");
  };

  if (totpStep != null) {
    const canSubmit = usingRecoveryCode
      ? recoveryCode.trim().length >= 10
      : totpCode.replace(/\D/g, "").length === 6;
    return (
      <div className={cn("flex flex-col gap-6", className)} {...props}>
        <Card className="overflow-hidden">
          <CardContent className="p-6 md:p-8">
            <form className="flex flex-col gap-4" onSubmit={(ev) => void handleSubmitTotp(ev)}>
              <h1 className="text-2xl font-bold text-foreground">
                {t("auth.login.totp.title")}
              </h1>
              <p className="text-muted-foreground">
                {usingRecoveryCode
                  ? t("auth.login.totp.recoveryInstructions")
                  : t("auth.login.totp.instructions")}
              </p>
              {usingRecoveryCode ? (
                <div className="grid gap-2">
                  <Label htmlFor="totp-recovery-code">
                    {t("auth.login.totp.recoveryLabel")}
                  </Label>
                  <Input
                    id="totp-recovery-code"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    placeholder={t("auth.login.totp.recoveryPlaceholder")}
                    className="text-center text-lg tracking-[0.2em] font-mono uppercase"
                    value={recoveryCode}
                    onChange={(ev) => setRecoveryCode(ev.target.value)}
                  />
                  <p className="text-sm text-muted-foreground">
                    {t("auth.login.totp.recoveryHint")}
                  </p>
                </div>
              ) : (
                <div className="grid gap-2">
                  <Label htmlFor="totp-code">{t("auth.login.totp.codeLabel")}</Label>
                  <CodeInput
                    id="totp-code"
                    autoFocus
                    value={totpCode}
                    onChange={setTotpCode}
                    disabled={totpBusy}
                  />
                </div>
              )}
              <Button
                type="submit"
                className="w-full transition-colors duration-150"
                disabled={totpBusy || !canSubmit}
              >
                {totpBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    {t("auth.login.totp.verifying")}
                  </>
                ) : (
                  t("auth.login.totp.verify")
                )}
              </Button>
              {/* No RESEND here, unlike the platform OTP step: nothing was
                  sent. The code lives in the authenticator app, and the only
                  alternative route is a recovery code. */}
              {/* A quiet text link, not a second full-width button. As an
                  outline Button this carried nearly the same weight as Verify,
                  which is wrong for a path most people never take — and every
                  reference renders it as a link. */}
              <button
                type="button"
                disabled={totpBusy}
                onClick={() => {
                  setUsingRecoveryCode((previous) => !previous);
                  setTotpCode("");
                  setRecoveryCode("");
                }}
                className="w-full text-center text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground disabled:opacity-60"
              >
                {usingRecoveryCode
                  ? t("auth.login.totp.useAuthenticator")
                  : t("auth.login.totp.useRecovery")}
              </button>
              <Button
                type="button"
                variant="ghost"
                className="w-full transition-colors duration-150"
                disabled={totpBusy}
                onClick={handleBackFromTotp}
              >
                <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
                {t("auth.login.totp.back")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (platformOtpStep != null) {
    return (
      <div className={cn("flex flex-col gap-6", className)} {...props}>
        <Card className="overflow-hidden">
          <CardContent className="p-6 md:p-8">
            <form className="flex flex-col gap-4" onSubmit={handleSubmitOtp}>
              <h1 className="text-2xl font-bold text-foreground">
                {t("auth.login.platformOtp.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("auth.login.platformOtp.instructions", {
                  email: platformOtpStep.email,
                })}
              </p>
              <div className="grid gap-2">
                <Label htmlFor="platform-otp-code">{t("auth.login.platformOtp.codeLabel")}</Label>
                <Input
                  id="platform-otp-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder={t("auth.login.platformOtp.codePlaceholder")}
                  className="text-center text-2xl tracking-[0.35em] font-mono"
                  value={otpCode}
                  onChange={(ev) => setOtpCode(ev.target.value.replace(/\D/g, "").slice(0, 6))}
                  aria-invalid={otpCode.length > 0 && otpCode.length < 6}
                />
              </div>
              <Button
                type="submit"
                className="w-full transition-colors duration-150"
                disabled={
                  otpVerifyBusy ||
                  otpResendBusy ||
                  otpCode.replace(/\D/g, "").length !== 6
                }
              >
                {otpVerifyBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    {t("auth.login.platformOtp.verifying")}
                  </>
                ) : (
                  t("auth.login.platformOtp.verify")
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full transition-colors duration-150"
                disabled={otpVerifyBusy || otpResendBusy}
                onClick={() => void handleResendOtp()}
              >
                {otpResendBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    {t("auth.login.platformOtp.resending")}
                  </>
                ) : (
                  t("auth.login.platformOtp.resend")
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full transition-colors duration-150"
                disabled={otpVerifyBusy || otpResendBusy}
                onClick={handleBackFromOtp}
              >
                {t("auth.login.platformOtp.back")}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-white/90">
          <Trans
            i18nKey="auth.login.legalAgreement"
            components={{
              terms: (
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white"
                />
              ),
              privacy: (
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white"
                />
              ),
            }}
          />
        </p>
      </div>
    );
  }

  if (sent) {
    return (
      <div className={cn("flex flex-col gap-6", className)} {...props}>
        <Card className="overflow-hidden">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col gap-4">
              <h1 className="text-2xl font-bold">
                {t("auth.login.checkEmail")}
              </h1>
              <p className="text-muted-foreground">
                {t("auth.login.checkEmailInstructions", {
                  email: sentEmail,
                  defaultValue: `We sent a sign-in link to ${sentEmail}. Click the link to sign in.`,
                })}
              </p>
              <Button variant="outline" onClick={handleResend}>
                {t("auth.login.backToLogin")}
              </Button>
            </div>
          </CardContent>
        </Card>
        <p className="text-center text-xs text-white/90">
          <Trans
            i18nKey="auth.login.legalAgreement"
            components={{
              terms: (
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white"
                />
              ),
              privacy: (
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white"
                />
              ),
            }}
          />
        </p>
      </div>
    );
  }

  const body = (
    <div className="flex flex-col gap-6">
            {formStep === "role" ? (
              <>
                <div className="flex flex-col items-center gap-2 text-center">
                  <h1 className="text-2xl font-bold">
                    {branding?.title ?? t("auth.login.title")}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {t("auth.login.roleSelectionTitle")}
                  </p>
                </div>
                {/* Leave fires on the GROUP, not each card: per-card leave made
                    moving from one card to the other emit user -> null ->
                    professional, three swaps in a few milliseconds, which read
                    as jank in the cover copy. */}
                <div
                  className="flex flex-col gap-3"
                  onMouseLeave={() => onRolePreview?.(null)}
                >
                  <button
                    type="button"
                    onMouseEnter={() => onRolePreview?.("user")}
                    onFocus={() => onRolePreview?.("user")}
                    onBlur={() => onRolePreview?.(null)}
                    onClick={() => handleRoleSelect("user")}
                    className="group flex items-center gap-4 rounded-xl border-2 border-border bg-background p-5 text-left transition-all duration-150 hover:border-primary hover:shadow-md dark:hover:bg-muted/30"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/20">
                      <User className="h-5 w-5" />
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-foreground">
                        {t("auth.login.roleUser")}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {t("auth.login.roleUserHint")}
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onMouseEnter={() => onRolePreview?.("professional")}
                    onFocus={() => onRolePreview?.("professional")}
                    onBlur={() => onRolePreview?.(null)}
                    onClick={() => handleRoleSelect("professional")}
                    className="group flex items-center gap-4 rounded-xl border-2 border-border bg-background p-5 text-left transition-all duration-150 hover:border-primary hover:shadow-md dark:hover:bg-muted/30"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/20">
                      <Briefcase className="h-5 w-5" />
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-foreground">
                        {t("auth.login.roleProfessional")}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {t("auth.login.roleProfessionalHint")}
                      </span>
                    </div>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col items-center gap-2 text-center">
                  <h1 className="text-2xl font-bold">
                    {branding?.title ?? t("auth.login.title")}
                  </h1>
                </div>
                {showLoginTypeSelector && (
                  /*
                    A CONTEXT LINE, not a floating bordered button. The old
                    control said "Cambiar" with no object — change what? — and
                    sat left-aligned under a centred heading as a stray box
                    competing with the submit button. Naming the chosen role
                    answers the question, and the change action is a quiet link
                    because almost nobody takes it.
                  */
                  <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <span>
                      {loginType === "professional"
                        ? t("auth.login.roleProfessional")
                        : t("auth.login.roleUser")}
                    </span>
                    <span aria-hidden className="opacity-40">·</span>
                    <button
                      type="button"
                      onClick={() => setFormStep("role")}
                      className="underline underline-offset-4 transition-colors hover:text-foreground"
                    >
                      {t("auth.login.changeRole")}
                    </button>
                  </p>
                )}
                {!hideGoogleSso && (
                  <>
                    <button
                      type="button"
                      disabled={ssoLoading}
                      onClick={() => {
                        setSsoLoading(true);
                        const ssoUrl = returnTo && returnTo !== "/"
                          ? `${apiBaseUrl}/auth/sso/google?returnTo=${encodeURIComponent(returnTo)}`
                          : `${apiBaseUrl}/auth/sso/google`;
                        window.location.href = ssoUrl;
                      }}
                      className={cn(
                        "inline-flex w-full items-center justify-center gap-2 rounded-md border bg-background px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50",
                      )}
                    >
                      {ssoLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                      ) : (
                        <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                      )}
                      {t("auth.sso.signInWithGoogle", "Login with Google")}
                    </button>
                    <div className="relative text-center text-sm">
                      <span className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </span>
                      <span className="relative bg-card px-2 text-muted-foreground">
                        {t("auth.login.orContinueWith", "Or continue with")}
                      </span>
                    </div>
                  </>
                )}
                <form
                  className="flex flex-col gap-4"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="grid gap-2">
                    <Label htmlFor="email">{t("auth.login.email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      {...register("email")}
                    />
                    {errors.email ? (
                      <p className="text-sm text-destructive">
                        {errors.email.message}
                      </p>
                    ) : null}
                  </div>
                  {mode === "password" && (
                    <div className="grid gap-2">
                      <Label htmlFor="password">{t("auth.login.password")}</Label>
                      <PasswordInput
                        id="password"
                        autoComplete="current-password"
                        placeholder="••••••••"
                        {...register("password")}
                      />
                      {errors.password ? (
                        <p className="text-sm text-destructive">
                          {errors.password.message}
                        </p>
                      ) : null}
                      <div className="flex justify-end">
                        <Link
                          to="/auth/forgot-password"
                          className="text-xs text-muted-foreground underline hover:text-foreground"
                        >
                          {t("auth.login.forgotPassword", "Forgot password?")}
                        </Link>
                      </div>
                    </div>
                  )}
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={
                      isSubmitting ||
                      (mode === "password" && !password)
                    }
                  >
                    {isSubmitting && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    )}
                    {isSubmitting
                      ? mode === "password"
                        ? t("auth.login.signingIn", "Signing in…")
                        : t("auth.login.submitting")
                      : mode === "password"
                        ? t("auth.login.signIn", "Sign in")
                        : t("auth.login.submit")}
                  </Button>
                </form>
                <div className="flex flex-col items-center gap-3 text-center">
                  {mode === "magic_link" ? (
                    <button
                      type="button"
                      className="text-sm text-muted-foreground underline hover:text-foreground transition-colors duration-150"
                      onClick={() => setMode("password")}
                    >
                      {t("auth.login.signInWithPassword", "Sign in with password")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="text-sm text-muted-foreground underline hover:text-foreground transition-colors duration-150"
                      onClick={() => setMode("magic_link")}
                    >
                      {t("auth.login.sendMagicLinkInstead", "Send magic link instead")}
                    </button>
                  )}
                  {!hideRegister && (
                    <p className="text-sm text-muted-foreground">
                      {t("auth.login.noAccount", "Don't have an account?")}{" "}
                      <Link
                        to="/register"
                        className="underline hover:text-foreground"
                      >
                        {t("auth.login.createNewAccount", "Sign up")}
                      </Link>
                    </p>
                  )}
                </div>
              </>
            )}
    </div>
  );

  /*
    BARE drops the card, the grid, the logo panel and the terms line, leaving
    only the form itself — the page supplies the shell. That is what lets
    login-page render the full-bleed 50/50 (decision ab0acd1) while
    enterprise-login-page keeps the contained card it was designed around.
    Default stays "card" so the existing caller is untouched.
  */
  if (chrome === "bare") return body;

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden">
        <CardContent className="grid p-0 md:grid-cols-2">
          <div className="p-6 md:p-8">{body}</div>
          <div className="hidden items-center justify-center bg-muted p-8 md:flex">
            <img
              src={branding?.logoUrl ?? "/brand/gestionalo-logo.svg"}
              alt={branding?.logoAlt ?? "Gestionalo"}
              className="max-w-xs w-2/3 object-contain"
            />
          </div>
        </CardContent>
      </Card>
      <p className="text-center text-xs text-white/90">
        <Trans
          i18nKey="auth.login.legalAgreement"
          components={{
            terms: (
              <a
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white"
              />
            ),
            privacy: (
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white"
              />
            ),
          }}
        />
      </p>
    </div>
  );
}
